import {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from 'ag-grid-community';
import {
  ColSize,
  createDateHasAllTimeCol,
  createDetailButtonColV2,
  createIdCol,
  createPhoneCol,
  createTypoCol,
} from '@/src/helpers/agColumnHelper';
import Badge from '@/components/atom/Badge';
import { getStatusBadgeProps } from '@/src/pages/RecyclingRequests/badge';
import {
  RecyclingRequestDto,
  RecyclingRequestProduct,
} from '@/src/models/RecyclingRequestDto';
import Button from '@/components/atom/Button';
import { BUTTON_CONST } from '@/src/constants/buttons';
import LocalIcon from '@/components/atom/Icons/LocalIcon';
import SearchSvg from '@/src/assets/icons/icon-search.svg';
import { UNIT_STRINGS } from '@/src/constants/unit';
import LucideIcon from '@/components/atom/Icons/LucideIcon';

export const recyclingRequestDeliveryColumn = ({
  onCopyClick,
  logisProvider,
  isAvailablePartialComplete,
}: {
  onCopyClick: (text: string) => void;
  logisProvider: string;
  isAvailablePartialComplete: boolean;
}): ColDef[] => [
  createTypoCol({
    field: 'index',
    pinned: 'left',
    size: 'xs',
    lockPosition: 'left',
    valueGetter: params => Number(params.node?.rowIndex) + 1,
    headerName: 'No.',
  }),
  createTypoCol({
    field: 'orderDetailNo',
    size: 'lg',
    headerName: '택배 주문 번호',
  }),
  createTypoCol({
    field: 'invoiceNo',
    size: 'lg',
    cellRenderer: ({ data }: ICellRendererParams) => {
      return (
        <div
          className="m-auto flex w-max items-center justify-center gap-[6px]"
          onClick={() => data.invoiceNo && onCopyClick(data.invoiceNo)}
        >
          {data.invoiceNo || '-'}
          {data.invoiceNo && <LucideIcon name="Copy" size={14} color="gray9" />}
        </div>
      );
    },
    headerName: '운송장 번호',
  }),
  createTypoCol({
    field: 'status',
    size: 'md',
    cellRenderer: ({ data }: ICellRendererParams) => {
      if (data.status === '미배달' || data.status === '미집하') {
        return `${data.status}${data.additionalStatus ? `(${data.additionalStatus})` : ''}${!isAvailablePartialComplete ? ' - 임의취소' : ''}`;
      }

      return data.status;
    },
    headerName: '배송 상태',
  }),
  {
    field: 'deliveryDetail',
    lockPosition: 'right',
    pinned: 'right',
    headerName: '배송현황 조회',
    headerClass: 'ag-right-aligned-cell',
    width: 129,
    minWidth: 129,
    maxWidth: 129,
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    cellRenderer: ({ data }: ICellRendererParams) => {
      return (
        <Button
          buttonSize="xSmall"
          buttonColor="outline"
          onClick={() => {
            window.open(
              logisProvider === 'LOTTE'
                ? 'https://www.lotteglogis.com/home/reservation/tracking/index'
                : `https://trace.cjlogistics.com/next/tracking.html?wblNo=${data.invoiceNo}`,
              '_blank',
            );
          }}
          disabled={!data.invoiceNo}
        >
          <LocalIcon
            iconSrc={SearchSvg}
            iconSize={18}
            className="mr-[4px] flex flex-shrink-0"
          />
          {BUTTON_CONST.lookUp}
        </Button>
      );
    },
  },
];

export const recyclingRequestProductOptionColumn = (): ColDef[] => [
  createTypoCol({
    field: 'index',
    pinned: 'left',
    size: 'xs',
    lockPosition: 'left',
    valueGetter: params => Number(params.node?.rowIndex) + 1,
    headerName: 'No.',
  }),
  createTypoCol({
    field: 'productName',
    size: 'md',
    maxWidth: 1000,
    flex: 1,
    headerName: '품목명',
  }),
  createTypoCol({
    field: 'optionName',
    size: 'md',
    maxWidth: 1000,
    flex: 1,
    headerName: '옵션',
  }),
  createTypoCol({
    field: 'count',
    size: 'sm',
    valueFormatter: params =>
      `${(params.data as RecyclingRequestProduct)?.count || 0}`,
    headerName: '수량',
  }),
  createTypoCol({
    field: 'weight',
    size: 'md',
    valueFormatter: params =>
      `${Number(Number((params.data as RecyclingRequestProduct)?.weight || 0).toFixed(2))}${
        UNIT_STRINGS.GRAM
      }`,
    headerName: '무게 (g)',
  }),
  createTypoCol({
    field: 'carbonEmissionReduction',
    size: 'md',
    valueFormatter: params =>
      `${Number(
        (
          (params.data as RecyclingRequestProduct)?.carbonEmissionReduction || 0
        ).toFixed(4),
      )}${UNIT_STRINGS.GRAM}`,
    headerName: '탄소 감축량 (g)',
  }),
  createTypoCol({
    field: 'pineScore',
    size: 'md',
    valueFormatter: params =>
      `${Number(
        ((params.data as RecyclingRequestProduct)?.pineScore || 0).toFixed(4),
      )}`,
    headerName: '소나무 기여',
  }),
];

export const recyclingRequestListColumn = (
  onDetailClick: (id: number) => void,
): ColDef[] => [
  createIdCol(),
  createDateHasAllTimeCol({
    field: 'createdAt',
    size: 'md',
    headerName: '신청 일시',
  }),
  createTypoCol({
    field: 'reqNo',
    size: 'md',
    headerName: '수거 번호',
  }),
  createTypoCol({
    field: 'nick',
    size: 'sm',
    valueFormatter: params => {
      return (params.data as RecyclingRequestDto)?.customer?.nick;
    },
    headerName: '닉네임',
  }),
  createTypoCol({
    field: 'requestName',
    size: 'sm',
    headerName: '보내는 사람',
  }),
  createPhoneCol({
    field: 'requestPhone',
    size: 'md',
    headerName: '수거 신청 핸드폰 번호',
  }),
  createTypoCol({
    field: 'customerType',
    size: 'sm',
    valueFormatter: params => {
      const customerType = (params.data as RecyclingRequestDto)?.customer?.type;
      return customerType === 'normal' ? '일반' : '사업자';
    },
    headerName: '회원 유형',
  }),
  {
    headerClass: 'ag-right-aligned-cell',
    cellStyle: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    field: 'status',
    ...ColSize.sm,
    cellRenderer: (params: ValueGetterParams) => {
      if (
        params?.data?.status === 'PROCESSING' &&
        params?.data?.isAvailablePartialComplete
      ) {
        return <Badge badgeColor="lightPurple">부분 수거중</Badge>;
      }
      return <Badge {...getStatusBadgeProps(params?.data?.status)} />;
    },
    headerName: '수거 진행 상태',
  },
  createTypoCol({
    field: 'boxCount',
    size: 'xs',
    headerName: '박스 수',
  }),
  createTypoCol({
    field: 'requestAddress',
    size: 'lg',
    headerName: '수거 주소',
  }),
  createDetailButtonColV2(onDetailClick),
];
